<template>
  <div>
    <Head :title="`${data.id}. ${data.last_name} ${data.first_name}`" />
    <h1 class="mb-8 text-3xl font-bold">
      <Link class="text-gray-400 hover:text-gray-600" href="/applicants">{{ $t('applicants.applicants') }}</Link>
      <span class="text-gray-400 font-medium">/ </span>
      {{ data.id }}. {{ data.last_name }} {{ data.first_name }}
    </h1>
    <div class="grid gap-4 lg:grid-cols-2 grid-cols-1">
      <detail-view class="row-span-3" :data="detailView">
        <template #default="item">
          <div v-if="item.props.key === 'note'" class="flex">
            {{ item.props.value }}
          </div>
        </template>
      </detail-view>
      <detail-view :data="localisationView" />
      <div class="bg-white rounded-md shadow p-5 pb-0">
        <div class="flex gap-5 items-center mb-5">
          <h3 class="text-xl font-medium leading-6 text-gray-900 block w-full">{{ $t('applicants.situation_case') }}</h3>
        </div>
        <Table
          :resource="situationCases"
          name="situationCases"
          class="custom-table-layout rounded"
        >
          <template #extraButtons>
            <Link class="btn-indigo inline-flex items-center mr-2" :href="`/applicants/${data.id}/situation-cases/create`" style="height: 38px">
              <icon name="plus" class="w-4 h-4 fill-current text-white mr-2" />
              <span class="capitalize">{{ $t('global.new') }}</span>
            </Link>
          </template>
          <template #cell(date_imprisonment)="{ item: situationCase }">
            {{ $filters.formatDate(situationCase.date_imprisonment) }}
          </template>
          <template #cell(interned)="{ item: situationCase }">
            <yes-no :value="!!situationCase.interned" />
          </template>
        </Table>
      </div>
      <div class="bg-white rounded-md shadow p-5">
        <div class="flex gap-5 items-center mb-5">
          <h3 class="text-xl font-medium leading-6 text-gray-900 block w-full">{{ $t('applicants.justice_workers') }}</h3>
        </div>
        <Table
          :resource="justiceWorkers"
          name="justiceWorkers"
          class="custom-table-layout rounded"
        >
          <template #extraButtons>
            <Link class="btn-indigo inline-flex items-center mr-2" :href="`/applicants/${data.id}/justice-workers/create`" style="height: 38px">
              <icon name="plus" class="w-4 h-4 fill-current text-white mr-2" />
              <span class="capitalize">{{ $t('global.new') }}</span>
            </Link>
          </template>
          <template #cell(date)="{ item: justiceWorker }">
            {{ $filters.formatDate(justiceWorker.date) }}
          </template>
        </Table>
      </div>
    </div>
    <div class="lg:col-span-2 mt-5">
      <div class="flex gap-5 items-center mb-5">
        <h2 class="font-bold text-2xl">{{ $t('treatments.treatments') }}</h2>
        <Link
          class="bg-green-500 hover:bg-green-700 text-black text-center py-3 px-4 rounded-full h-12 w-12 inline-flex items-center"
          :href="`/applicants/${data.id}/treatments/create`"
        >
          <icon name="plus" class="h-5 w-5 fill-current text-white" />
        </Link>
      </div>
      <Table
        :resource="treatments"
        name="treatments"
        class="custom-table-layout rounded"
      >
        <template #cell(date_open)="{ item: treatment }">
          {{ $filters.formatDate(treatment.date_open) }}
        </template>
        <template #cell(type)="{ item: treatment }">
          {{$t('treatment-types.'+treatment.type)}}
        </template>
        <template #cell(date_closed)="{ item: treatment }">
          {{ (treatment.status.value == 3) ? $filters.formatDate(treatment.date_closed) : '' }}
        </template>
        <template #cell(latest_intervention)="{ item: treatment }">
          {{ $filters.formatDate(treatment.latest_intervention) }}
        </template>
        <template #cell(status)="{ item: treatment }">
          <status-badge :status="treatment.status" />
        </template>
      </Table>
    </div>
    <div class="bg-white rounded-md shadow p-5">
      <div class="flex gap-5 items-center mb-5">
        <h2 class="font-bold text-2xl">{{ $t('global.files') }}</h2>
        <button class="bg-gray-400 hover:bg-gray-500 text-white font-bold py-1 px-3 rounded mt-2" data-modal-toggle="defaultModal" @click="showModal = !showModal">
          {{ $t('global.add_files') }}
        </button>
        <FileUploadModal v-model="showModal" :model="data" :upload-url="uploadUrl" />
      </div>
      <vue-good-table
        style-class="vgt-table"
        :columns="columns"
        :rows="files"
        :pagination-options="{enabled: true}"
        :sort-options="{
          enabled: true,
          initialSortBy: {field: 'file.created_at', type: 'desc'}
        }"
      >
        <template #table-row="props">
          <span v-if="props.column.field == 'file.created_at'">
            {{ $filters.formatDateTime(props.row.file.created_at) }}
          </span>
          <span v-else-if="props.column.field == 'file.file_name'">
            <a
              v-if="$store.getters.can(props.row.type.toLowerCase()) || $store.getters.can('admin') || props.row.type.toLowerCase() == 'bénéficiaire'"
              :href="'/downloads/download/' + props.row.file.id" style="text-decoration: underline"
              class="text-indigo-500"
            >
              {{ props.row.file.file_name }}
            </a>
            <span v-else>
              {{ props.row.file.file_name }}
            </span>
            <span class="vgt-pull-right" v-if="props.row.type.toLowerCase() == 'bénéficiaire'">
              <button class="text-red-600 hover:underline" tabindex="-1" type="button" @click="destroyFile(props.row.file.uuid)">{{$t('global.delete')}}</button>
            </span>

          </span>
          <span v-else-if="props.column.field == 'type'">
            <span v-if="props.row.type.toLowerCase() != 'bénéficiaire'">
              <a

                v-if="$store.getters.can(props.row.type.toLowerCase()) || $store.getters.can('admin')"
                :href="props.row.link" style="text-decoration: underline"
                class="text-indigo-500"
              >
                {{ props.row.type }}
              </a>
              <span v-else>
                {{ props.row.type }}
              </span>
            </span>
            <span v-else>
              {{ props.row.type}}
            </span>

          </span>
        </template>
      </vue-good-table>
    </div>
  </div>
</template>

<script>
import {Head, Link} from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import DetailView from '@/Shared/DetailView'
import YesNo from '@/Shared/YesNo'
import Icon from '@/Shared/Icon'
import StatusBadge from '@/Shared/StatusBadge'
import FileUploadModal from '@/Shared/FileUploadModal'
import Table from '@/Shared/Table/Components/Table'
import 'vue-good-table-next/dist/vue-good-table-next.css'
import {VueGoodTable} from 'vue-good-table-next'
import { isEmpty, replace } from 'lodash'

export default {
  components: {
    DetailView,
    YesNo,
    Head,
    Link,
    Icon,
    StatusBadge,
    Table,
    VueGoodTable,
    FileUploadModal,
  },
  layout: Layout,
  props: {
    data: Object,
    treatments: Object,
    situationCases: Object,
    justiceWorkers: Object,
    files: Array,
  },
  data() {
    return {
      showModal: false,
      detailView: {
        items: [
          {
            label: this.$t('applicants.first_name'),
            value: this.data.first_name,
          },
          {
            label: this.$t('applicants.last_name'),
            value: this.data.last_name,
          },
          {
            label: this.$t('applicants.date_birth'),
            value: this.$filters.formatDate(this.data.date_birth),
          },
          {
            label: this.$t('applicants.birth_place'),
            value: this.data.birth_place,
          },
          {
            label: this.$t('applicants.nationality'),
            value: this.data.nationality_name,
          },
          {
            label: this.$t('applicants.national_registry'),
            value: this.data.national_registry,
          },
          {
            label: this.$t('applicants.email'),
            value: this.data.email,
          },
          {
            label: this.$t('applicants.residence_permit'),
            value: this.data.residence_permit,
          },
          {
            label: this.$t('applicants.income'),
            value: this.data.income,
          },
          {
            label: this.$t('applicants.mutuality'),
            value: this.data.mutuality,
          },
          {
            label: this.$t('applicants.syndicate'),
            value: this.data.syndicate,
          },
          {
            label: this.$t('applicants.gender'),
            value: this.data.gender_name,
          },
          {
            label: this.$t('applicants.civil_state'),
            value: this.data.civil_state_name,
          },
          {
            label: this.$t('applicants.languages'),
            value: (!isEmpty(this.data.languages)) ? this.data.languages?.join(', ') : null,
          },
          {
            label: this.$t('applicants.phone'),
            value: (!isEmpty(this.data.phone)) ? this.data.phone?.join(', ') : null,
          },
          {
            label: this.$t('applicants.residence'),
            value: (this.data.street) ? this.data.street + ' ' + this.data.street_number + ', ' + this.data.zip_code + ' ' + this.data.city : null,
          },
          {
            label: this.$t('applicants.note'),
            value: this.data.note,
            key: 'note',
          },
        ],
        updateLink: `/applicants/${this.data.id}/edit`,
        header: {
          title: this.$t('applicant.information.info'),
          text: this.$t('applicant.information.sub'),
          textTwo: (this.data.closed) ? this.$t('applicants.closed.sub') : null
        },
      },
      localisationView: {
        items: [
          {
            label: this.$t('applicants.current_location_type'),
            value: this.data.actual_location_value,
          },
          {
            hide: !this.data.actual_location,
            label: this.$t('applicants.prison'),
            value: this.data.actual_location + ' ' + ((this.data.actual_location_name) ? ' - ' + this.data.actual_location_name : ''),
          },
          {
            hide: (!!this.data.actual_location || !this.data.actual_location_name),
            label: this.$t('applicants.place'),
            value: this.data.actual_location_name,
          },
          {
            label: this.$t('applicants.last_edit'),
            value: this.$filters.formatDateTime(this.data.location_updated_at),
          },
        ],
        updateLink: `/applicants/${this.data.id}/current-location/edit`,
        header: {
          title: this.$t('applicants.current_location.information'),
          text: this.$t('applicants.current_location.sub'),
        },
      },
      columns: [
        {
          label: 'Type',
          field: 'type',
          filterOptions: {
            styleClass: 'class1', // class to be added to the parent th element
            enabled: true,
            placeholder: 'All',
            filterDropdownItems: ['PANE', 'Social', 'Psy', 'MDD', 'Logement', 'Bénévole', 'Bénéficiaire'],
          },
        },
        {
          label: 'File',
          field: 'file.file_name',
          filterOptions: {
            styleClass: 'class1', // class to be added to the parent th element
            enabled: true,
            placeholder: 'Search...',
          },
        },
        {
          label: 'User',
          field: 'user',
        },
        {
          label: 'Upload',
          field: 'file.created_at',
        },
      ],
    }
  },
  computed: {
    uploadUrl() {
      return `/applicants/${this.data.id}/upload-file`
    },
  },
  methods: {
    destroyFile(id) {
      this.$swal().then(result => {
        if (result.value) {
          this.$inertia.delete(`/applicants/${this.data.id}/remove-file/${id}`)
        }
      })
    },
  },
}
</script>
